import React from "react";

export type FeatureMosaicTilesLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type FeatureMosaicTilesImage = {
  id: string | number;
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  href?: string;
  ariaLabel?: string;
  objectPosition?: string;
};

export type FeatureMosaicTilesCta = {
  label: string;
  href: string;
  ariaLabel?: string;
};

export type FeatureMosaicTilesProps = {
  eyebrow?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  cta?: FeatureMosaicTilesCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
  images?: FeatureMosaicTilesImage[];
  Link?: FeatureMosaicTilesLinkComponent;
  dark?: boolean;
  reverse?: boolean;
  showDivider?: boolean;
  accentColor?: string;
  accentBorderColor?: string;
  backgroundColor?: string;
  textColor?: string;
  mutedTextColor?: string;
  maxWidth?: number | string;
  className?: string;
  ariaLabel?: string;
};

const featureMosaicTilesCss = `
.feature-mosaic-tiles {
  --feature-mosaic-bg: #030303;
  --feature-mosaic-text: #ffffff;
  --feature-mosaic-muted: rgba(255, 255, 255, 0.72);
  --feature-mosaic-accent: #6d6cff;
  --feature-mosaic-accent-border: rgba(109, 108, 255, 0.42);
  width: 100%;
  background: var(--feature-mosaic-bg);
  color: var(--feature-mosaic-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.feature-mosaic-tiles,
.feature-mosaic-tiles * {
  box-sizing: border-box;
}
.feature-mosaic-tiles-shell {
  width: 100%;
  margin: 0 auto;
  padding: 56px 24px 28px;
}
.feature-mosaic-tiles-layout {
  display: grid;
  grid-template-columns: minmax(270px, 0.78fr) minmax(0, 1.44fr);
  align-items: center;
  gap: 58px;
}
.feature-mosaic-tiles-reverse .feature-mosaic-tiles-copy {
  order: 2;
}
.feature-mosaic-tiles-reverse .feature-mosaic-tiles-media {
  order: 1;
}
.feature-mosaic-tiles-copy {
  max-width: 430px;
}
.feature-mosaic-tiles-eyebrow {
  margin: 0 0 14px;
  color: var(--feature-mosaic-accent);
  font-size: 12px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}
.feature-mosaic-tiles-title {
  margin: 0;
  color: var(--feature-mosaic-text);
  font-size: 37px;
  line-height: 1.17;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: pre-line;
}
.feature-mosaic-tiles-description {
  max-width: 390px;
  margin: 24px 0 0;
  color: var(--feature-mosaic-muted);
  font-size: 15px;
  line-height: 1.66;
  font-weight: 500;
  letter-spacing: 0;
}
.feature-mosaic-tiles-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  max-width: 100%;
  margin-top: 30px;
  padding: 0 22px;
  border: 1px solid var(--feature-mosaic-accent-border);
  border-radius: 3px;
  background: linear-gradient(180deg, rgba(25, 18, 70, 0.88) 0%, rgba(10, 7, 38, 0.94) 100%);
  color: var(--feature-mosaic-text);
  font-size: 12px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
}
.feature-mosaic-tiles-cta:hover,
.feature-mosaic-tiles-cta:focus-visible {
  border-color: var(--feature-mosaic-accent);
  transform: translateY(-1px);
}
.feature-mosaic-tiles-cta:focus-visible {
  outline: 3px solid var(--feature-mosaic-accent-border);
  outline-offset: 4px;
}
.feature-mosaic-tiles-media {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-auto-rows: 188px;
  gap: 8px;
  min-width: 0;
}
.feature-mosaic-tiles-media-item {
  position: relative;
  display: block;
  min-width: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  text-decoration: none;
}
.feature-mosaic-tiles-media-item-0 {
  grid-column: span 2;
}
.feature-mosaic-tiles-media-item-1 {
  grid-column: span 2;
}
.feature-mosaic-tiles-media-item-2 {
  grid-column: span 3;
}
.feature-mosaic-tiles-media-item-3 {
  grid-column: span 1;
}
.feature-mosaic-tiles-media-item-extra {
  grid-column: span 1;
}
.feature-mosaic-tiles-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--feature-mosaic-image-position, center);
  transform: scale(1);
  transition: transform 420ms ease, filter 420ms ease;
}
.feature-mosaic-tiles-media-item:hover .feature-mosaic-tiles-image,
.feature-mosaic-tiles-media-item:focus-visible .feature-mosaic-tiles-image {
  transform: scale(1.045);
  filter: saturate(1.08);
}
.feature-mosaic-tiles-media-item:focus-visible {
  outline: 3px solid var(--feature-mosaic-accent-border);
  outline-offset: 3px;
}
.feature-mosaic-tiles-image-fallback {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 18px;
  color: var(--feature-mosaic-muted);
  font-size: 13px;
  line-height: 1.4;
  font-weight: 700;
  text-align: center;
}
.feature-mosaic-tiles-divider {
  width: 100%;
  height: 1px;
  margin-top: 24px;
  background: rgba(255, 255, 255, 0.14);
}
.feature-mosaic-tiles-light {
  --feature-mosaic-bg: #ffffff;
  --feature-mosaic-text: #101828;
  --feature-mosaic-muted: #475467;
  --feature-mosaic-accent-border: rgba(15, 23, 42, 0.14);
}
.feature-mosaic-tiles-light .feature-mosaic-tiles-cta {
  background: #101828;
  color: #ffffff;
}
.feature-mosaic-tiles-light .feature-mosaic-tiles-media-item {
  border-color: rgba(15, 23, 42, 0.08);
  background: #eef2f6;
}
.feature-mosaic-tiles-light .feature-mosaic-tiles-divider {
  background: rgba(15, 23, 42, 0.12);
}
@media (max-width: 1023px) {
  .feature-mosaic-tiles-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 38px;
  }
  .feature-mosaic-tiles-reverse .feature-mosaic-tiles-copy,
  .feature-mosaic-tiles-reverse .feature-mosaic-tiles-media {
    order: initial;
  }
  .feature-mosaic-tiles-copy {
    max-width: 640px;
  }
  .feature-mosaic-tiles-description {
    max-width: 560px;
  }
}
@media (max-width: 767px) {
  .feature-mosaic-tiles-shell {
    padding: 42px 28px 0;
  }
  .feature-mosaic-tiles-layout {
    gap: 52px;
  }
  .feature-mosaic-tiles-eyebrow {
    margin-bottom: 18px;
    font-size: 12px;
  }
  .feature-mosaic-tiles-title {
    font-size: 34px;
    line-height: 1.28;
  }
  .feature-mosaic-tiles-description {
    margin-top: 26px;
    font-size: 17px;
    line-height: 1.68;
  }
  .feature-mosaic-tiles-cta {
    width: 100%;
    min-height: 68px;
    margin-top: 32px;
    padding: 0 18px;
    font-size: 15px;
  }
  .feature-mosaic-tiles-media {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-auto-rows: auto;
    gap: 20px;
  }
  .feature-mosaic-tiles-media-item,
  .feature-mosaic-tiles-media-item-0,
  .feature-mosaic-tiles-media-item-1,
  .feature-mosaic-tiles-media-item-2,
  .feature-mosaic-tiles-media-item-3,
  .feature-mosaic-tiles-media-item-extra {
    grid-column: auto;
    aspect-ratio: 1.12 / 1;
  }
  .feature-mosaic-tiles-divider {
    margin-top: 42px;
  }
}
@media (max-width: 380px) {
  .feature-mosaic-tiles-shell {
    padding-left: 20px;
    padding-right: 20px;
  }
  .feature-mosaic-tiles-title {
    font-size: 30px;
  }
  .feature-mosaic-tiles-description {
    font-size: 15px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function getImageSrc(image: FeatureMosaicTilesImage): string {
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: FeatureMosaicTilesImage): string {
  return image.alt || image.title || "";
}

function resolveCta({
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
}: Pick<FeatureMosaicTilesProps, "cta" | "ctaLabel" | "ctaHref" | "ctaAriaLabel">) {
  if (cta) return cta;
  if (!ctaLabel || !ctaHref) return undefined;

  return {
    label: ctaLabel,
    href: ctaHref,
    ariaLabel: ctaAriaLabel,
  };
}

function AnchorLink({
  href,
  children,
  ...props
}: {
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
  [key: string]: any;
}) {
  return (
    <a href={href} {...props}>
      {children}
    </a>
  );
}

export default function FeatureMosaicTiles({
  eyebrow,
  title,
  description,
  cta,
  ctaLabel,
  ctaHref,
  ctaAriaLabel,
  images = [],
  Link,
  dark = true,
  reverse = false,
  showDivider = true,
  accentColor = "#6d6cff",
  accentBorderColor = "rgba(109, 108, 255, 0.42)",
  backgroundColor,
  textColor,
  mutedTextColor,
  maxWidth = 1180,
  className = "",
  ariaLabel,
}: FeatureMosaicTilesProps) {
  const resolvedCta = resolveCta({ cta, ctaLabel, ctaHref, ctaAriaLabel });
  const ResolvedLink = Link || AnchorLink;
  const hasCopy = !!eyebrow || !!title || !!description || !!resolvedCta;

  if (!hasCopy && !images.length) return null;

  return (
    <section
      className={[
        "feature-mosaic-tiles",
        dark ? "" : "feature-mosaic-tiles-light",
        reverse ? "feature-mosaic-tiles-reverse" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={ariaLabel || (typeof title === "string" ? title : undefined)}
      style={
        {
          "--feature-mosaic-bg": backgroundColor,
          "--feature-mosaic-text": textColor,
          "--feature-mosaic-muted": mutedTextColor,
          "--feature-mosaic-accent": accentColor,
          "--feature-mosaic-accent-border": accentBorderColor,
        } as React.CSSProperties
      }
    >
      <style>{featureMosaicTilesCss}</style>

      <div className="feature-mosaic-tiles-shell" style={{ maxWidth: getCssSize(maxWidth) }}>
        <div className="feature-mosaic-tiles-layout">
          {hasCopy ? (
            <div className="feature-mosaic-tiles-copy">
              {eyebrow ? <p className="feature-mosaic-tiles-eyebrow">{eyebrow}</p> : null}
              {title ? <h2 className="feature-mosaic-tiles-title">{title}</h2> : null}
              {description ? <p className="feature-mosaic-tiles-description">{description}</p> : null}

              {resolvedCta ? (
                <ResolvedLink
                  href={resolvedCta.href}
                  className="feature-mosaic-tiles-cta"
                  aria-label={resolvedCta.ariaLabel || resolvedCta.label}
                >
                  {resolvedCta.label}
                </ResolvedLink>
              ) : null}
            </div>
          ) : null}

          {images.length ? (
            <div className="feature-mosaic-tiles-media" aria-label="Feature images">
              {images.map((image, index) => {
                const imageSrc = getImageSrc(image);
                const imageTitle = image.title || `Feature image ${index + 1}`;
                const tileClassName = [
                  "feature-mosaic-tiles-media-item",
                  `feature-mosaic-tiles-media-item-${index}`,
                  index > 3 ? "feature-mosaic-tiles-media-item-extra" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                const tileStyle = {
                  "--feature-mosaic-image-position": image.objectPosition,
                } as React.CSSProperties;

                const media = (
                  <>
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={getImageAlt(image)}
                        className="feature-mosaic-tiles-image"
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    ) : (
                      <div className="feature-mosaic-tiles-image-fallback">{imageTitle}</div>
                    )}
                  </>
                );

                if (image.href) {
                  return (
                    <ResolvedLink
                      key={image.id}
                      href={image.href}
                      className={tileClassName}
                      title={imageTitle}
                      aria-label={image.ariaLabel || imageTitle}
                      style={tileStyle}
                    >
                      {media}
                    </ResolvedLink>
                  );
                }

                return (
                  <div key={image.id} className={tileClassName} style={tileStyle}>
                    {media}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        {showDivider ? <div className="feature-mosaic-tiles-divider" /> : null}
      </div>
    </section>
  );
}
